import { isEmpty } from 'underscore';
import { PodcastTemplateConfig } from 'redux/middleware/api/podcast-service';
import { updateLayerReferences } from 'utils/embed/config-transforms';
import measurement from 'utils/measurement';
import { Layer, SoundwaveState } from '../types';

export function getSoundwave(
  config: PodcastTemplateConfig,
  layers: Layer[],
): SoundwaveState {
  const { soundwave } = config;

  if (!soundwave || isEmpty(soundwave)) {
    return undefined;
  }

  const layer = layers[soundwave.layerId];

  if (layer?.type !== 'waveform') {
    throw new Error(
      `Expected waveform asset to reference layer of type "waveform" but got ${layer?.type}`,
    );
  }

  const soundwaveState = {
    color: soundwave.style?.color,
    fidelity: 'lo-fi' as const,
    // some templaets (e.g. minimalSquare) have things like "height: 0" in the
    // config.  force the unitless value to vh if it's a raw number
    height: measurement(soundwave.style?.height, 'vh'),
    layerId: layer.id,
    left: measurement(soundwave.position?.properties?.left, 'vw'),
    original: undefined,
    top: measurement(soundwave.position?.properties?.top),
    type: soundwave?.style?.pattern,
    width: measurement(soundwave.style?.width),
  };

  return soundwaveState;
}

/*
 * In the editor, you can have a waveform track with no waveform on it.  In the
 * configuration, this is modeled by having the waveform track id in config.layerOrder.
 * Since everything in the VideoTemplateEditor is added in a new layer, if the user
 * loads one of these configurations and then adds a waveform, an additional
 * waveform layer will get added, causing issues when the template/project is opened
 * (multiple waveform tracks are not supported).
 *
 * This function removes the waveform layer from the list of layers if the waveform
 * asset is disabled.  All layer references are updated to account for the waveform
 * layer that's removed
 */
export function removeDisabledSoundwave(
  config: PodcastTemplateConfig,
): PodcastTemplateConfig {
  if (
    !config.soundwave ||
    isEmpty(config.soundwave) ||
    config.soundwave.enabled
  ) {
    return config;
  }

  const waveformLayerIndex = config.layerOrder.indexOf('waveform');

  if (waveformLayerIndex < 0) {
    // no waveform layer. since the waveform is disabled there's no issue loading
    // this configuration, but remove the waveform config since it's not needed and
    // follows how the VideoTemplateEditor handles not having a waveform
    const { soundwave: _, ...restConfig } = config;
    return restConfig;
  }

  // remove soundwave layer, delete soundwave, fix layers (everything below the
  // waveform layer shifts up by 1)
  const updatedLayerOrder = [...config.layerOrder];
  updatedLayerOrder.splice(waveformLayerIndex, 1);

  const { soundwave: _, ...restConfig } = config;
  return updateLayerReferences(
    {
      ...restConfig,
      layerOrder: updatedLayerOrder,
    },
    asset => {
      // the asset points to an index in the original layerOrder which contains
      // the waveform track.  if the layer index comes after the waveform layer,
      // it has to be shifted up 1
      return asset.layerId > waveformLayerIndex
        ? asset.layerId - 1
        : asset.layerId;
    },
  );
}
